import { z } from 'zod';

import { PERMISSION_KEYS, ROLES, type Permissions, type Role } from '../domain.js';
import { emailSchema } from './auth.js';
import { idSchema, optionalText, paginationQuerySchema } from './common.js';

/**
 * Papéis que um gestor do órgão pode conceder.
 *
 * `owner` fica de fora: só quem já é proprietário transfere a propriedade, e
 * por rota própria. Sem isso, qualquer administrador se promoveria.
 */
export const ASSIGNABLE_ROLES = ROLES.filter((role) => role !== 'owner');

const permissionsShape = Object.fromEntries(
  PERMISSION_KEYS.map((key) => [key, z.boolean()]),
) as Record<keyof Permissions, z.ZodBoolean>;

/** Todas as permissões, opcionais: o que não vier mantém o valor atual. */
export const permissionsSchema = z.object(permissionsShape).partial();
export type PermissionsInput = z.infer<typeof permissionsSchema>;

export const createUserBodySchema = z.object({
  name: z.string().trim().min(2, 'Informe o nome completo').max(120),
  email: emailSchema,
  role: z.enum(ASSIGNABLE_ROLES as [Role, ...Role[]]),
  jobTitle: optionalText(120),
  permissions: permissionsSchema.optional(),
});
export type CreateUserBody = z.infer<typeof createUserBodySchema>;

export const updateUserBodySchema = z.object({
  name: z.string().trim().min(2).max(120).optional(),
  /**
   * E-mail é a credencial de entrada da pessoa, que pertence a um órgão só
   * (ADR 057). Está aqui porque corrigir um e-mail digitado errado no cadastro
   * é justamente o que trava o primeiro acesso, e o gestor que cadastrou é quem
   * percebe.
   */
  email: emailSchema.optional(),
  role: z.enum(ASSIGNABLE_ROLES as [Role, ...Role[]]).optional(),
  jobTitle: optionalText(120),
  /** `suspended` bloqueia o acesso. Não existe exclusão de usuário. */
  status: z.enum(['active', 'suspended']).optional(),
  permissions: permissionsSchema.optional(),
});
export type UpdateUserBody = z.infer<typeof updateUserBodySchema>;

/**
 * A própria conta: nome e e-mail.
 *
 * Não traz papel nem permissão — ninguém se promove. A senha tem rota própria,
 * que exige a atual.
 *
 * `currentPassword` não é exigida para trocar o nome, e é para trocar o e-mail:
 * o e-mail é por onde se entra **e** para onde vai o link de recuperação. Sem a
 * confirmação, quem alcançasse uma sessão aberta por dois minutos trocaria o
 * endereço e passaria a poder recuperar a conta quando quisesse — o acesso
 * momentâneo viraria posse permanente.
 */
export const updateProfileBodySchema = z
  .object({
    name: z.string().trim().min(2, 'Informe o nome').max(120).optional(),
    email: emailSchema.optional(),
    currentPassword: z.string().min(1).optional(),
  })
  .refine(
    (body) => body.name !== undefined || body.email !== undefined,
    'Informe ao menos um campo para alterar.',
  );
export type UpdateProfileBody = z.infer<typeof updateProfileBodySchema>;

export interface ProfileResult {
  readonly id: string;
  readonly name: string;
  readonly email: string;
  readonly avatarUrl: string | null;
}

export const listUsersQuerySchema = paginationQuerySchema.extend({
  search: z.string().trim().max(120).optional(),
  role: z.enum(ROLES).optional(),
  status: z.enum(['active', 'invited', 'suspended']).optional(),
});
export type ListUsersQuery = z.infer<typeof listUsersQuerySchema>;

export const userIdParamSchema = z.object({ userId: idSchema });

// ─────────────────────────────────────────────────────── respostas

export interface UserEntry {
  readonly id: string;
  readonly name: string;
  readonly email: string;
  readonly avatarUrl: string | null;
  readonly role: Role;
  readonly jobTitle: string | null;
  /** `invited` = ainda não definiu senha; `suspended` = acesso bloqueado. */
  readonly status: 'active' | 'invited' | 'suspended';
  readonly permissions: Permissions;
  readonly lastLoginAt: string | null;
  /** Obras em que é fiscal designado. */
  readonly obrasCount: number;
  readonly reportsCount: number;
  readonly createdAt: string;
}

/**
 * Link de definição de senha.
 *
 * Vem no corpo porque o envio de e-mail só entra na Fase 6: até lá, quem
 * cadastra copia e entrega pelo canal que já usa. Aparece uma única vez — o
 * servidor guarda apenas o hash do token.
 */
export interface IssuedLink {
  readonly url: string;
  readonly expiresAt: string;
  readonly email: string;
}

/**
 * O que o gestor recebe ao cadastrar alguém ou redefinir a senha.
 *
 * Devolve a senha em claro de propósito: é ela que o gestor vai ditar. O banco
 * guarda só o hash, e esta é a única vez que o valor trafega.
 */
export interface IssuedPassword {
  readonly email: string;
  /** A senha padrão: toda conta nasce aqui, no órgão de quem cadastra (ADR 057). */
  readonly password: string;
}
